import React, { useState, useEffect } from 'react';
import { fmtDateTimeShort, fmtDate } from '@shared/formatDate';
import {
  Box,
  Paper,
  List,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Link,
  Checkbox,
} from '@mui/material';
import {
  Mail as MailIcon,
  DraftsOutlined as DraftsOutlinedIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  DoNotDisturb as DoNotDisturbIcon,
  Timer as TimerIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  LockOpen as LockOpenIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  AttachFile as AttachFileIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { MINOR_MESSAGE_MUTATION_BLOCKED } from '../../utils/minorMessagePolicy';
import { Message, MessageStatus, Transaction, UserCommercialSettings } from '../../types';
import { PromoTransaction } from '../../services/accountService';
import messageService from '../../services/messageService';
import accountService from '../../services/accountService';
import api from '../../services/api';
import type { TransactionDetailDto } from '@shared/transactionDetailTypes';
import TransactionDetailsDialog from '../shared/TransactionDetailsDialog';
import PromoTransactionDetailsDialog from '../shared/PromoTransactionDetailsDialog';
import LocalReceiptPreviewDialog from './LocalReceiptPreviewDialog';
import StorageTierBadge from './StorageTierBadge';
import RetrievalModal from './RetrievalModal';
import OffloadModal from './OffloadModal';
import { hasMessage } from '../../services/offloadedMessageStore';
import { deleteDraft, listDrafts } from '../../services/draftMessageStore';
import {
  getCachedNickname,
  seedContactIdentityCache,
} from '../../services/contactIdentityCache';
import { AdjustCategoryRateDialog, AdjustSenderRateDialog } from './AdjustPostageRateDialogs';
import { formatInboxFromLabel, formatListSenderLabel } from '../../utils/qixitAddress';
import { messageMatchesAlias } from '../../utils/messageAliasFilter';
import {
  clearMessageFilterCriteria,
  hasActiveMessageFilters,
  loadMessageFilterCriteria,
  saveMessageFilterCriteria,
  type MessageFilterDateRange,
  type MessageFilterStatus,
} from '../../utils/messageFilterStore';
import socialService, { type ProfileAliasDto } from '../../services/socialService';
import MessageSearchFilterBar from './MessageSearchFilterBar';

interface MessageListProps {
  folder: 'inbox' | 'sent' | 'credits' | 'debits' | 'archive' | 'trash';
  onMessageSelect: (mailId: string) => void;
  compactLandscape?: boolean;
}

const getStatusIcon = (status: MessageStatus) => {
  switch (status) {
    case 'pending':
      return <HourglassEmptyIcon fontSize="small" color="warning" />;
    case 'authorized':
    case 'read':
      return <CheckCircleIcon fontSize="small" color="success" />;
    case 'inprocess':
      return <ScheduleIcon fontSize="small" color="info" />;
    case 'expired':
      return <ErrorIcon fontSize="small" color="error" />;
    case 'canceled':
      return <DoNotDisturbIcon fontSize="small" color="disabled" />;
    case 'rejected':
      return <DoNotDisturbIcon fontSize="small" color="error" />;
    case 'error':
      return <ErrorIcon fontSize="small" color="error" />;
    default:
      return <MailIcon fontSize="small" />;
  }
};

const getStatusColor = (status: MessageStatus): 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' => {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'authorized':
    case 'read':
      return 'success';
    case 'inprocess':
      return 'info';
    case 'expired':
    case 'error':
    case 'rejected':
      return 'error';
    case 'canceled':
      return 'default';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: MessageStatus): string => {
  switch (status) {
    case 'pending':
      return 'In Inbox';
    case 'authorized':
      return 'Read';
    case 'read':
      return 'Read';
    case 'inprocess':
      return 'Processing';
    case 'expired':
      return 'Expired';
    case 'canceled':
      return 'Canceled';
    case 'rejected':
      return 'Rejected';
    case 'error':
      return 'Error';
    default:
      return status;
  }
};

// Cache messages per folder page for instant display on revisit.
const folderCache = new Map<string, { messages: Message[]; totalCount: number; totalPages: number; lastFetched: number }>();
const CACHE_TTL_MS = 30_000;
const PERSISTED_CACHE_TTL_MS = 60_000;
const PERSISTED_CACHE_VERSION = 'v1';

type PersistedPagePayload = {
  version: string;
  storedAt: number;
  folder: MessageListProps['folder'];
  page: number;
  pageSize: number;
  username: string;
  totalCount: number;
  totalPages: number;
  messages: Message[];
};

const getPersistedCacheKey = (
  username: string,
  folder: MessageListProps['folder'],
  page: number,
  pageSize: number
) => `mailbox:messages:${PERSISTED_CACHE_VERSION}:${username}:${folder}:${page}:${pageSize}`;

const readPersistedPage = (
  username: string,
  folder: MessageListProps['folder'],
  page: number,
  pageSize: number
): PersistedPagePayload | null => {
  try {
    const raw = localStorage.getItem(getPersistedCacheKey(username, folder, page, pageSize));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedPagePayload;
    if (!parsed || parsed.version !== PERSISTED_CACHE_VERSION) return null;
    if (Date.now() - parsed.storedAt > PERSISTED_CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writePersistedPage = (
  username: string,
  folder: MessageListProps['folder'],
  page: number,
  pageSize: number,
  payload: { messages: Message[]; totalCount: number; totalPages: number }
) => {
  try {
    const record: PersistedPagePayload = {
      version: PERSISTED_CACHE_VERSION,
      storedAt: Date.now(),
      folder,
      page,
      pageSize,
      username,
      messages: payload.messages,
      totalCount: payload.totalCount,
      totalPages: payload.totalPages,
    };
    localStorage.setItem(getPersistedCacheKey(username, folder, page, pageSize), JSON.stringify(record));
  } catch {
    // Ignore storage quota or serialization issues.
  }
};

const removePersistedPage = (
  username: string,
  folder: MessageListProps['folder'],
  page: number,
  pageSize: number
) => {
  try {
    localStorage.removeItem(getPersistedCacheKey(username, folder, page, pageSize));
  } catch {
    // Ignore storage errors.
  }
};

// Cache transactions globally to persist across folder switches
const transactionCache = new Map<string, Transaction>();

const MessageList: React.FC<MessageListProps> = ({ folder, onMessageSelect, compactLandscape = false }) => {
  const queryClient = useQueryClient();
  const { user, isMinor } = useAuth();
  const usernameKey = user?.username || 'anonymous';
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshing, setRefreshing] = useState(false); // Subtle indicator for background refresh
  const pageSize = 50;

  // Blob retrieval modal state
  const [retrievalModalOpen, setRetrievalModalOpen] = useState(false);
  const [retrievalMessage, setRetrievalMessage] = useState<{
    mailId: string; subject: string; blobTier: string; blobSizeBytes?: number | null;
  } | null>(null);

  // Phase 3: Offload modal state
  const [offloadModalOpen, setOffloadModalOpen] = useState(false);
  const [offloadMessage, setOffloadMessage] = useState<{
    mailId: string; subject: string; fromName: string; sentDate: string; sizeBytes?: number | null;
  } | null>(null);

  // Phase 3: track which OFFLOADED messages are in local IndexedDB
  const [localMailIds, setLocalMailIds] = useState<Set<string>>(new Set());

  const mapDraftToMessage = (draft: ReturnType<typeof listDrafts>[number]): Message => ({
    mailId: `draft:${draft.id}`,
    fromAddress: user?.username || 'local',
    fromName: user?.username || 'Draft',
    toRecipients: draft.toRecipients ?? [],
    ccRecipients: draft.ccRecipients ?? [],
    bccRecipients: draft.bccRecipients ?? [],
    subject: draft.subject || '(No Subject)',
    sentDate: draft.updatedAt,
    markRead: true,
    hasAttachments: false,
    totalAmount: draft.transactionAmount ?? 0,
    moneyTotal: 0,
    commercialMailing: false,
    deliveryFee: draft.maxDeliveryFee ?? 0,
    requiredPostage: 0,
    attachedAmount: draft.transactionAmount ?? 0,
    maxDeliveryFee: draft.maxDeliveryFee ?? 0,
    expirationDays: draft.expirationDays ?? 90,
    status: 'pending',
    statusDescription: 'Draft',
    isDelivered: false,
    canCancel: false,
    canRefund: false,
    canDelete: true,
    canExpireEarly: false,
    isCanceled: false,
    isRefunded: false,
    isExpired: false,
    recipientCount: (draft.toRecipients?.length ?? 0) + (draft.ccRecipients?.length ?? 0) + (draft.bccRecipients?.length ?? 0),
    readCount: 0,
    isSystemMessage: false,
    isDraft: true,
    draftUpdatedAt: draft.updatedAt,
  });

  // Check IndexedDB for any OFFLOADED messages when list changes
  useEffect(() => {
    const offloaded = messages.filter(m => m.blobTier === 'OFFLOADED');
    if (offloaded.length === 0) return;

    Promise.all(offloaded.map(m => hasMessage(m.mailId).then(found => ({ mailId: m.mailId, found }))))
      .then(results => {
        const found = new Set(results.filter(r => r.found).map(r => r.mailId));
        setLocalMailIds(found);
      })
      .catch(() => {});
  }, [messages]);

  useEffect(() => {
    api.get<import('../../types').Contact[]>('/api/contacts')
      .then((data) => {
        if (Array.isArray(data)) seedContactIdentityCache(data);
      })
      .catch(() => {});
  }, []);

  // Cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellingMessageId, setCancellingMessageId] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Expire early dialog state
  const [expireDialogOpen, setExpireDialogOpen] = useState(false);
  const [expiringMessageId, setExpiringMessageId] = useState<string | null>(null);
  const [expireLoading, setExpireLoading] = useState(false);

  // Archive state (no longer needed for loading spinner, but kept for potential future use)
  const [archiveLoading, setArchiveLoading] = useState<string | null>(null);

  // Restore from trash state
  const [restoreLoading, setRestoreLoading] = useState<string | null>(null);

  // Release reserves state
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [releaseResult, setReleaseResult] = useState<{ expiredCount: number; releasedAmount: number } | null>(null);

  // Trash/Reject state
  const [trashLoading, setTrashLoading] = useState(false);

  // Transaction Details dialog state
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [transactionLoading, setTransactionLoading] = useState(false);
  
  // Vendor Credit Transaction Details dialog state (for purchases using vendor credits)
  const [promoDialogOpen, setPromoDialogOpen] = useState(false);
  const [selectedPromoTransaction, setSelectedPromoTransaction] = useState<PromoTransaction | null>(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptDetail, setReceiptDetail] = useState<TransactionDetailDto | null>(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  
  // State to trigger re-render when transactions are loaded
  const [transactionCacheVersion, setTransactionCacheVersion] = useState(0);

  // Rate adjustment dialog state
  const [adjustSenderRateOpen, setAdjustSenderRateOpen] = useState(false);
  const [adjustCategoryRateOpen, setAdjustCategoryRateOpen] = useState(false);
  const [rateAdjustMessage, setRateAdjustMessage] = useState<Message | null>(null);

  // Pending message warning dialog state
  const [pendingWarningOpen, setPendingWarningOpen] = useState(false);
  const [pendingMessageId, setPendingMessageId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<'archive' | 'trash' | null>(null);
  
  // Search and filter state (persisted in sessionStorage per user)
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<MessageFilterStatus>('all');
  const [filterDateRange, setFilterDateRange] = useState<MessageFilterDateRange>('all');
  const [filterAlias, setFilterAlias] = useState('');
  const [searchSortMode, setSearchSortMode] = useState(false);
  const [filterCriteriaLoaded, setFilterCriteriaLoaded] = useState(false);
  const [profileAliases, setProfileAliases] = useState<ProfileAliasDto[]>([]);

  useEffect(() => {
    const loaded = loadMessageFilterCriteria(usernameKey);
    setSearchQuery(loaded.searchQuery);
    setFilterStatus(loaded.filterStatus);
    setFilterDateRange(loaded.filterDateRange);
    setFilterAlias(loaded.filterAlias);
    if (hasActiveMessageFilters(loaded)) {
      setSearchSortMode(true);
    }
    setFilterCriteriaLoaded(true);
  }, [usernameKey]);

  useEffect(() => {
    if (!filterCriteriaLoaded) return;
    saveMessageFilterCriteria(usernameKey, {
      searchQuery,
      filterStatus,
      filterDateRange,
      filterAlias,
    });
  }, [usernameKey, searchQuery, filterStatus, filterDateRange, filterAlias, filterCriteriaLoaded]);

  useEffect(() => {
    if (!user) return;
    socialService.getMyAliases()
      .then(setProfileAliases)
      .catch(() => setProfileAliases([]));
  }, [user?.username]);

  const hasActiveFilters = hasActiveMessageFilters({
    searchQuery,
    filterStatus,
    filterDateRange,
    filterAlias,
  });

  useEffect(() => {
    if (hasActiveFilters) {
      setSearchSortMode(true);
    }
  }, [hasActiveFilters]);

  const toggleSearchSortMode = () => {
    if (searchSortMode && hasActiveFilters) return;
    setSearchSortMode(prev => !prev);
  };

  const handleClearFilters = () => {
    const cleared = clearMessageFilterCriteria(usernameKey);
    setSearchQuery(cleared.searchQuery);
    setFilterStatus(cleared.filterStatus);
    setFilterDateRange(cleared.filterDateRange);
    setFilterAlias(cleared.filterAlias);
    setSearchSortMode(false);
  };
  
  // Selection state
  const [selectedMessages, setSelectedMessages] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const cacheKey = `${usernameKey}:${folder}:page:${currentPage}:size:${pageSize}`;
  
  // Filter messages based on search and filter criteria
  const filterMessages = (msgs: Message[]): Message[] => {
    let filtered = msgs;

    // Search filter (subject, from, to)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(msg => 
        msg.subject.toLowerCase().includes(query) ||
        msg.fromName?.toLowerCase().includes(query) ||
        msg.fromAddress?.toLowerCase().includes(query) ||
        msg.toRecipients?.some(to => to.toLowerCase().includes(query))
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      if (filterStatus === 'read') {
        filtered = filtered.filter(msg => msg.markRead);
      } else if (filterStatus === 'unread') {
        filtered = filtered.filter(msg => !msg.markRead);
      } else {
        filtered = filtered.filter(msg => msg.status === filterStatus);
      }
    }

    // Date range filter
    if (filterDateRange !== 'all') {
      const now = new Date();
      const cutoffDate = new Date();
      switch (filterDateRange) {
        case 'today':
          cutoffDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        case 'year':
          cutoffDate.setFullYear(now.getFullYear() - 1);
          break;
      }
      filtered = filtered.filter(msg => {
        const msgDate = new Date(msg.receivedDate || msg.sentDate);
        return msgDate >= cutoffDate;
      });
    }

    if (filterAlias.trim()) {
      filtered = filtered.filter(msg => messageMatchesAlias(msg, filterAlias));
    }

    return filtered;
  };

  const getFolderTitle = () => {
    switch (folder) {
      case 'inbox': return 'Inbox';
      case 'sent': return 'Sent';
      case 'credits': return 'Credits / Ad Revenue';
      case 'debits': return 'Debits / Purchases';
      case 'archive': return 'Archive';
      case 'trash': return 'Trash';
      default: return folder;
    }
  };
  
  // No longer need sender overrides cache - we use postageRateType from message/transaction instead

  useEffect(() => {
    setCurrentPage(1);
  }, [folder]);

  useEffect(() => {
    const forceFreshForFinancialFolders = folder === 'credits' || folder === 'debits';
    if (forceFreshForFinancialFolders) {
      setLoading(true);
      loadMessages(false);
      return;
    }
    const cached = folderCache.get(cacheKey);
    const isFresh = cached && (Date.now() - cached.lastFetched) < CACHE_TTL_MS;
    if (isFresh) {
      setMessages(cached.messages);
      setTotalCount(cached.totalCount);
      setTotalPages(cached.totalPages);
      setLoading(false);
      setError(null);
      setRefreshing(true);
      loadMessages(true);
      return;
    }
    if (user?.username) {
      const persisted = readPersistedPage(user.username, folder, currentPage, pageSize);
      if (persisted) {
        setMessages(persisted.messages);
        setTotalCount(persisted.totalCount);
        setTotalPages(persisted.totalPages);
        setLoading(false);
        setError(null);
        setRefreshing(true);
        loadMessages(true);
        return;
      }
    }
    setLoading(true);
    loadMessages(false);
  }, [folder, currentPage, user?.username]);

  // Clear selection and close search/sort mode when folder changes
  useEffect(() => {
    setSelectedMessages(new Set());
    setSearchQuery('');
    setFilterStatus('all');
    setFilterDateRange('all');
    setSearchSortMode(false); // Close search/sort when switching folders
  }, [folder]);

  // Pre-fetch transactions for credits/debits messages to get vendor/buyer info
  useEffect(() => {
    if ((folder === 'credits' || folder === 'debits') && messages.length > 0) {
      // For credits/debits, use sourceTransactionId (the actual transaction ID) if available
      // This is parsed from OriginalId on the backend and is more reliable than mailId
      const messagesToFetch = messages.filter(msg => {
        const lookupId = msg.sourceTransactionId || msg.mailId;
        return !transactionCache.has(`${folder}:${lookupId}`);
      });
      if (messagesToFetch.length > 0) {
        // Defer and batch enrichment so rows render immediately and requests are smoother.
        const BATCH_SIZE = 10;
        const DELAY_MS = 250;
        const run = async () => {
          for (let i = 0; i < messagesToFetch.length; i += BATCH_SIZE) {
            const chunk = messagesToFetch.slice(i, i + BATCH_SIZE);
            await Promise.all(
              chunk.map(msg => getTransactionForMessage(msg.sourceTransactionId || msg.mailId))
            );
            if (i + BATCH_SIZE < messagesToFetch.length) {
              await new Promise(resolve => setTimeout(resolve, DELAY_MS));
            }
          }
        };
        run().catch(() => {
          // Silently fail - fallback labels remain available.
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder, messages]);

  const loadMessages = async (backgroundRefresh: boolean = false) => {
    if (!backgroundRefresh) {
      setLoading(true);
      setMessages([]);
    } else {
      setRefreshing(true);
    }
    setError(null);
    
    try {
      let response;
      if (folder === 'inbox') {
        response = await messageService.getInbox(currentPage, pageSize);
      } else if (folder === 'sent') {
        response = await messageService.getSent(currentPage, pageSize);
      } else if (folder === 'credits') {
        response = await messageService.getCredits(currentPage, pageSize);
      } else if (folder === 'archive') {
        response = await messageService.getArchive(currentPage, pageSize);
      } else if (folder === 'trash') {
        response = await messageService.getTrash(currentPage, pageSize);
      } else {
        response = await messageService.getDebits(currentPage, pageSize);
      }
      
      // Sort inbox messages: unread first, then by date descending
      let sortedMessages = response.messages;
      if (folder === 'inbox') {
        sortedMessages = [...response.messages].sort((a, b) => {
          // Unread messages first
          if (!a.markRead && b.markRead) return -1;
          if (a.markRead && !b.markRead) return 1;
          // Then sort by date descending
          const dateA = new Date(a.receivedDate || a.sentDate || 0).getTime();
          const dateB = new Date(b.receivedDate || b.sentDate || 0).getTime();
          return dateB - dateA;
        });
      }
      
      // Deduplicate messages by mailId to prevent duplicate keys
      const deduplicateMessages = (msgList: Message[]) => {
        const seen = new Set<string>();
        return msgList.filter(msg => {
          if (seen.has(msg.mailId)) {
            return false;
          }
          seen.add(msg.mailId);
          return true;
        });
      };
      
      const finalMessages = deduplicateMessages(sortedMessages);
      const draftMessages = folder === 'sent' && currentPage === 1 && user?.username
        ? listDrafts(user.username).map(mapDraftToMessage)
        : [];
      const displayMessages = [...draftMessages, ...finalMessages];
      setMessages(displayMessages);
      const responseTotalCount = (response.totalCount ?? finalMessages.length) + draftMessages.length;
      const responseTotalPages = response.totalPages ?? Math.max(1, Math.ceil(responseTotalCount / pageSize));
      setTotalCount(responseTotalCount);
      setTotalPages(responseTotalPages);
      folderCache.set(cacheKey, {
        messages: displayMessages,
        totalCount: responseTotalCount,
        totalPages: responseTotalPages,
        lastFetched: Date.now()
      });
      if (user?.username) {
        writePersistedPage(user.username, folder, currentPage, pageSize, {
          messages: displayMessages,
          totalCount: responseTotalCount,
          totalPages: responseTotalPages,
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleManualRefresh = () => {
    folderCache.delete(cacheKey);
    if (user?.username) {
      removePersistedPage(user.username, folder, currentPage, pageSize);
    }
    loadMessages(false);
  };

  const handleCancelClick = (e: React.MouseEvent, mailId: string) => {
    e.stopPropagation();
    setCancellingMessageId(mailId);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancellingMessageId) return;
    
    setCancelLoading(true);
    try {
      await messageService.cancelMessage(cancellingMessageId);
      
      // Invalidate balance cache to trigger refresh
      queryClient.invalidateQueries({ queryKey: ['accountBalance', user?.username] });
      
      // Invalidate cache and refresh the list
      folderCache.delete(cacheKey);
      loadMessages(false);
      setCancelDialogOpen(false);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to cancel message';
      setError(errorMessage);
    } finally {
      setCancelLoading(false);
      setCancellingMessageId(null);
    }
  };

  const handleExpireClick = (e: React.MouseEvent, mailId: string) => {
    e.stopPropagation();
    setExpiringMessageId(mailId);
    setExpireDialogOpen(true);
  };

  const handleConfirmExpire = async () => {
    if (!expiringMessageId) return;
    
    setExpireLoading(true);
    try {
      await messageService.expireMessage(expiringMessageId);
      
      // Invalidate balance cache to trigger refresh
      queryClient.invalidateQueries({ queryKey: ['accountBalance', user?.username] });
      
      // Invalidate cache and refresh the list
      folderCache.delete(cacheKey);
      loadMessages(false);
      setExpireDialogOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to expire message');
    } finally {
      setExpireLoading(false);
      setExpiringMessageId(null);
    }
  };

  const handleArchiveClick = async (e: React.MouseEvent, mailId: string, message?: Message) => {
    e.stopPropagation();
    if (isMinor) {
      setError(MINOR_MESSAGE_MUTATION_BLOCKED);
      return;
    }
    
    // Check if message is pending (awaiting delivery) - must be expired first
    if (message && message.canCancel && folder === 'sent') {
      setPendingMessageId(mailId);
      setPendingAction('archive');
      setPendingWarningOpen(true);
      return;
    }
    
    // Immediately remove from local state for instant UI update
    setMessages(prev => prev.filter(msg => msg.mailId !== mailId));
    
    try {
      await messageService.archiveMessage(mailId);
      // API call succeeded - message already removed from UI
    } catch (err: any) {
      // API call failed - restore the message to the list
      if (message) {
        setMessages(prev => {
          // Insert back in the same position (or at the end if we can't determine position)
          const index = prev.findIndex(msg => msg.mailId === mailId);
          if (index === -1) {
            return [...prev, message];
          }
          return [...prev.slice(0, index), message, ...prev.slice(index)];
        });
      }
      // Extract error message from API response
      const errorMessage = err.response?.data?.message || err.message || 'Failed to archive message';
      setError(errorMessage);
    }
  };

  const handleRestoreClick = async (e: React.MouseEvent, mailId: string) => {
    e.stopPropagation();
    if (isMinor) {
      setError(MINOR_MESSAGE_MUTATION_BLOCKED);
      return;
    }
    setRestoreLoading(mailId);
    try {
      // Use different restore method based on folder
      if (folder === 'archive') {
        await messageService.restoreFromArchive(mailId);
      } else {
        await messageService.restoreFromTrash(mailId);
      }
      // Invalidate cache and refresh the list
      folderCache.delete(cacheKey);
      setMessages([]);
      await loadMessages(false);
    } catch (err: any) {
      // Extract error message from API response
      const errorMessage = err.response?.data?.message || err.message || 'Failed to restore message';
      setError(errorMessage);
    } finally {
      setRestoreLoading(null);
    }
  };

  const handleShowDetails = async (e: React.MouseEvent, messageOrMailId: string) => {
    e.stopPropagation();
    setTransactionLoading(true);
    const sourceMessage = messages.find(
      m => m.mailId === messageOrMailId || m.sourceTransactionId === messageOrMailId
    );
    const lookupId = sourceMessage?.sourceTransactionId || messageOrMailId;
    const receiptMailId = sourceMessage?.mailId || messageOrMailId;
    const cacheKey = `${folder}:${lookupId}`;
    try {
      // Check cache first (folder-aware key prevents debit/credit mix-up when same sourceTransactionId)
      if (transactionCache.has(cacheKey)) {
        setSelectedTransaction(transactionCache.get(cacheKey)!);
        setTransactionDialogOpen(true);
        setTransactionLoading(false);
        return;
      }
      
      let transactions = await accountService.getTransactionsByMailId(lookupId);
      if ((!transactions || transactions.length === 0) && receiptMailId !== lookupId) {
        transactions = await accountService.getTransactionsByMailId(receiptMailId);
      }
      if (transactions && transactions.length > 0) {
        // When a user is both buyer and affiliate, the same source ID has both a debit and a credit
        // record. Pick the one matching the current folder to avoid showing the wrong transaction.
        const transaction = folder === 'debits'
          ? (transactions.find(t => t.amount <= 0) ?? transactions[0])
          : folder === 'credits'
          ? (transactions.find(t => t.amount > 0) ?? transactions[0])
          : transactions[0];
        transactionCache.set(cacheKey, transaction);
        setSelectedTransaction(transaction);
        setTransactionDialogOpen(true);
      } else if (lookupId && /^[0-9a-f-]{36}$/i.test(lookupId)) {
        // Detail API accepts account-history id or linked mailId (chrAdjustmentSrcID).
        setSelectedTransaction({ transactionId: lookupId } as Transaction);
        setTransactionDialogOpen(true);
      } else if (sourceMessage?.promoTransactionId) {
        await handleShowPromoDetails(e, sourceMessage.promoTransactionId);
      } else {
        const statusHint = `${sourceMessage?.statusDescription || sourceMessage?.status || ''}`.toLowerCase();
        if (statusHint.includes('failed') || statusHint.includes('canceled') || statusHint.includes('orphaned')) {
          setError('No transaction details were created for this message because delivery failed or it was canceled before completion.');
        } else {
          setError('No transaction details found for this message yet.');
        }
      }
    } catch (err: any) {
      console.error('Failed to load transaction details:', err);
    } finally {
      setTransactionLoading(false);
    }
  };

  const isIdentityDisclosureSystemNotice = (message: Message): boolean => {
    const subject = (message.subject || '').toLowerCase();
    const fromText = `${message.fromName || ''} ${message.fromAddress || ''}`.toLowerCase();
    const fromTrustHub = fromText.includes('qixit trust hub') || fromText.includes('system@');
    return Boolean(subject.includes('id verification request') && fromTrustHub);
  };

  /** System notices cannot be rejected (no postage to return); trash/delete only. */
  const inboxMessageSupportsReject = (message: Message): boolean =>
    !message.isSystemMessage && !isIdentityDisclosureSystemNotice(message);

  const handleOpenLocalReceipt = async (message: Message) => {
    const transactionId = message.sourceTransactionId;
    if (!transactionId) {
      onMessageSelect(message.mailId);
      return;
    }

    const username = user?.username || 'anonymous';
    const cacheKey = `receipt-detail:v2:${username}:${transactionId}`;

    setReceiptError(null);
    setReceiptLoading(true);
    setReceiptDialogOpen(true);

    const markLocalMessageRead = () => {
      setMessages(prev =>
        prev.map(m =>
          m.mailId === message.mailId
            ? { ...m, markRead: true, readDate: m.readDate || new Date().toISOString(), readCount: Math.max(1, m.readCount || 0), status: m.status === 'authorized' ? 'read' : m.status, statusDescription: m.statusDescription === 'Commission credited' ? 'Commission credited (read)' : m.statusDescription }
            : m
        )
      );
    };

    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        setReceiptDetail(JSON.parse(cached) as TransactionDetailDto);
        markLocalMessageRead();
        setReceiptLoading(false);
        return;
      }

      const detail = await api.get<TransactionDetailDto>(
        `/api/account/transactions/detail/${encodeURIComponent(transactionId)}`
      );
      setReceiptDetail(detail);
      localStorage.setItem(cacheKey, JSON.stringify(detail));
      markLocalMessageRead();
    } catch (err: any) {
      // Legacy fallback: open stored message body if local receipt generation fails.
      setReceiptDialogOpen(false);
      setReceiptDetail(null);
      setReceiptError(null);
      onMessageSelect(message.mailId);
    } finally {
      setReceiptLoading(false);
    }
  };
  
  // Handler for showing vendor credit transaction details (for purchases using vendor credits)
  const handleShowPromoDetails = async (e: React.MouseEvent, promoTransactionId: string) => {
    e.stopPropagation();
    setPromoLoading(true);
    try {
      // Fetch promo transaction history and find the specific transaction
      const promoHistory = await accountService.getPromoHistory(100);
      const promoTx = promoHistory.find(tx => tx.transactionId === promoTransactionId);
      if (promoTx) {
        const sourceMessage = messages.find(msg => msg.promoTransactionId === promoTransactionId);
        const fallbackRegularTransactionId = sourceMessage?.sourceTransactionId;
        let fallbackRegularCreditsUsed = 0;
        if (fallbackRegularTransactionId) {
          const cachedRegular = transactionCache.get(`${folder}:${fallbackRegularTransactionId}`);
          const regularTx = cachedRegular ?? await getTransactionForMessage(fallbackRegularTransactionId);
          fallbackRegularCreditsUsed = Math.abs(regularTx?.amount ?? 0);
        }

        setSelectedPromoTransaction({
          ...promoTx,
          linkedRegularTransactionId: promoTx.linkedRegularTransactionId || fallbackRegularTransactionId,
          regularCreditsUsed: promoTx.regularCreditsUsed ?? fallbackRegularCreditsUsed,
          splitSource: promoTx.linkedRegularTransactionId ? promoTx.splitSource : 'template-fallback',
        });
        setPromoDialogOpen(true);
      }
    } catch (err: any) {
      console.error('Failed to load promo transaction details:', err);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleOpenLinkedRegularTransaction = (transactionId: string) => {
    setPromoDialogOpen(false);
    setSelectedPromoTransaction(null);
    setSelectedTransaction({ transactionId } as Transaction);
    setTransactionDialogOpen(true);
  };
  
  // Helper to get transaction for a message (from cache or fetch if needed)
  const getTransactionForMessage = async (mailId: string): Promise<Transaction | null> => {
    const cacheKey = `${folder}:${mailId}`;
    if (transactionCache.has(cacheKey)) {
      return transactionCache.get(cacheKey)!;
    }
    try {
      const transactions = await accountService.getTransactionsByMailId(mailId);
      if (transactions && transactions.length > 0) {
        const transaction = folder === 'debits'
          ? (transactions.find(t => t.amount <= 0) ?? transactions[0])
          : folder === 'credits'
          ? (transactions.find(t => t.amount > 0) ?? transactions[0])
          : transactions[0];
        transactionCache.set(cacheKey, transaction);
        // Trigger re-render to update display with transaction data
        setTransactionCacheVersion(prev => prev + 1);
        return transaction;
      }
    } catch (err) {
      console.error('Failed to load transaction:', err);
    }
    return null;
  };

  // Handler to adjust sender-specific postage rate
  const handleAdjustSenderRate = (e: React.MouseEvent, message: Message) => {
    e.stopPropagation();
    setRateAdjustMessage(message);
    setAdjustSenderRateOpen(true);
  };

  // Handler to adjust category-wide postage rate
  const handleAdjustCategoryRate = (e: React.MouseEvent, message: Message) => {
    e.stopPropagation();
    setRateAdjustMessage(message);
    setAdjustCategoryRateOpen(true);
  };

  const handleRateAdjustSaved = () => {
    folderCache.delete(cacheKey);
    loadMessages(false);
  };

  const handleReleaseReserves = async () => {
    if (isMinor) {
      setError('Bulk reserve release is managed by a parent account.');
      return;
    }
    setReleaseLoading(true);
    try {
      const result = await messageService.releaseAllReserves();
      if (result.success) {
        setReleaseResult({ expiredCount: result.expiredCount, releasedAmount: result.releasedAmount });
        // Invalidate balance cache to trigger refresh
        queryClient.invalidateQueries({ queryKey: ['accountBalance', user?.username] });
        // Invalidate cache and refresh the list
        folderCache.delete(cacheKey);
        loadMessages(false);
      } else {
        setError('Failed to release reserves');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to release reserves');
    } finally {
      setReleaseLoading(false);
    }
  };

  const handleTrashClick = async (e: React.MouseEvent, mailId: string, isRead: boolean, message?: Message) => {
    e.stopPropagation();
    if (isMinor) {
      setError(MINOR_MESSAGE_MUTATION_BLOCKED);
      return;
    }
    
    // Check if message is pending (awaiting delivery) - must be expired first
    if (message && message.canCancel && folder === 'sent') {
      setPendingMessageId(mailId);
      setPendingAction('trash');
      setPendingWarningOpen(true);
      return;
    }
    
    // Immediately remove from local state for instant UI update
    setMessages(prev => prev.filter(msg => msg.mailId !== mailId));
    
    try {
      if (folder === 'sent') {
        await messageService.trashMessage(mailId);
      } else if (folder !== 'inbox' || isRead || (message && !inboxMessageSupportsReject(message))) {
        // Read, non-inbox, or system message — move to trash (system messages cannot be rejected)
        await messageService.trashMessage(mailId);
      } else {
        // Message was NOT read - reject it (return funds to sender)
        await messageService.rejectMessage(mailId);
        // Invalidate balance cache (though recipient doesn't get funds, sender does)
        queryClient.invalidateQueries({ queryKey: ['accountBalance', user?.username] });
      }
      // API call succeeded - message already removed from UI
    } catch (err: any) {
      // API call failed - restore the message to the list
      if (message) {
        setMessages(prev => {
          // Insert back in the same position (or at the end if we can't determine position)
          const index = prev.findIndex(msg => msg.mailId === mailId);
          if (index === -1) {
            return [...prev, message];
          }
          return [...prev.slice(0, index), message, ...prev.slice(index)];
        });
      }
      // Extract error message from API response
      const errorMessage = err.response?.data?.message || err.message || 'Failed to process message';
      setError(errorMessage);
    }
  };

  const formatDate = (dateString: string) => fmtDate(dateString);

  if (loading && messages.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  const filteredMessages = filterMessages(messages);
  
  // Reference transactionCacheVersion to ensure React re-renders when transactions are loaded
  // This ensures the "From:" and "Paid to:" lines update when transaction data arrives
  void transactionCacheVersion;

  const searchFilterBar = searchSortMode ? (
    <MessageSearchFilterBar
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      filterStatus={filterStatus}
      onFilterStatusChange={setFilterStatus}
      filterDateRange={filterDateRange}
      onFilterDateRangeChange={setFilterDateRange}
      filterAlias={filterAlias}
      onFilterAliasChange={setFilterAlias}
      aliases={profileAliases}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={handleClearFilters}
    />
  ) : null;

  const searchSortToggleLink = (
    <Link
      component="button"
      variant="body2"
      onClick={toggleSearchSortMode}
      sx={{
        cursor: 'pointer',
        textDecoration: 'none',
        color: hasActiveFilters ? 'warning.main' : 'primary.main',
        '&:hover': {
          textDecoration: 'underline',
        },
        fontSize: '0.875rem',
      }}
    >
      {searchSortMode ? 'Hide search and sort' : 'Search and sort'}
    </Link>
  );
  
  if (messages.length === 0 && !hasActiveFilters && !searchSortMode) {
    return (
      <Box display="flex" flexDirection="column" minHeight="400px">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, pb: 0 }}>
          {searchSortToggleLink}
        </Box>
        <Box display="flex" flexGrow={1} justifyContent="center" alignItems="center">
          <Typography variant="h6" color="text.secondary">
            No messages in {folder}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (messages.length === 0 && (hasActiveFilters || searchSortMode)) {
    return (
      <Paper sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        <Box sx={{ p: { xs: 0.75, sm: 2 }, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6">
              {getFolderTitle()} (0)
            </Typography>
            {searchSortToggleLink}
          </Box>
          {searchFilterBar}
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="text.secondary">
            No messages in {folder}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (filteredMessages.length === 0 && hasActiveFilters) {
    return (
      <Paper sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        <Box sx={{ p: { xs: 0.75, sm: 2 }, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6">
              {getFolderTitle()} (0)
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (of {messages.length})
              </Typography>
            </Typography>
            {searchSortToggleLink}
          </Box>
          {searchFilterBar}
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="text.secondary">
            No messages match your search/filter criteria
          </Typography>
        </Box>
      </Paper>
    );
  }

  const allFilteredSelected = (filtered: Message[]) => filtered.length > 0 && filtered.every(msg => selectedMessages.has(msg.mailId));
  const someFilteredSelected = (filtered: Message[]) => filtered.some(msg => selectedMessages.has(msg.mailId));

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newSelected = new Set(selectedMessages);
      filteredMessages.forEach(msg => newSelected.add(msg.mailId));
      setSelectedMessages(newSelected);
    } else {
      const newSelected = new Set(selectedMessages);
      filteredMessages.forEach(msg => newSelected.delete(msg.mailId));
      setSelectedMessages(newSelected);
    }
  };

  // Handle individual selection
  const handleSelectMessage = (mailId: string, checked: boolean) => {
    const newSelected = new Set(selectedMessages);
    if (checked) {
      newSelected.add(mailId);
    } else {
      newSelected.delete(mailId);
    }
    setSelectedMessages(newSelected);
  };

  // Bulk archive
  const handleBulkArchive = async () => {
    if (isMinor) {
      setError(MINOR_MESSAGE_MUTATION_BLOCKED);
      return;
    }
    const toArchive = Array.from(selectedMessages);
    if (toArchive.length === 0) return;

    setBulkActionLoading(true);
    try {
      // Remove from UI immediately
      setMessages(prev => prev.filter(msg => !selectedMessages.has(msg.mailId)));
      
      // Archive all in parallel
      await Promise.all(toArchive.map(id => messageService.archiveMessage(id)));
      
      // Clear selection
      setSelectedMessages(new Set());
    } catch (err: any) {
      setError(err.message || 'Failed to archive some messages');
      // Invalidate cache and reload to restore state
      folderCache.delete(cacheKey);
      loadMessages(false);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk delete/trash
  const handleBulkDelete = async () => {
    if (isMinor) {
      setError(MINOR_MESSAGE_MUTATION_BLOCKED);
      return;
    }
    const toDelete = Array.from(selectedMessages);
    if (toDelete.length === 0) return;

    setBulkActionLoading(true);
    try {
      // Get messages to check read status
      const messagesToDelete = messages.filter(msg => selectedMessages.has(msg.mailId));
      
      // Remove from UI immediately
      setMessages(prev => prev.filter(msg => !selectedMessages.has(msg.mailId)));
      
      // Delete/trash all in parallel
      await Promise.all(messagesToDelete.map(msg => {
        if (folder === 'sent') {
          if (msg.canCancel) {
            return messageService.deleteMessage(msg.mailId);
          }
          return messageService.trashMessage(msg.mailId);
        }
        if (folder === 'inbox' && !msg.markRead && inboxMessageSupportsReject(msg)) {
          return messageService.rejectMessage(msg.mailId);
        }
        return messageService.trashMessage(msg.mailId);
      }));
      
      // Invalidate balance cache
      queryClient.invalidateQueries({ queryKey: ['accountBalance', user?.username] });
      
      // Clear selection
      setSelectedMessages(new Set());
    } catch (err: any) {
      setError(err.message || 'Failed to delete some messages');
      // Invalidate cache and reload to restore state
      folderCache.delete(cacheKey);
      loadMessages(false);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk restore
  const handleBulkRestore = async () => {
    if (isMinor) {
      setError(MINOR_MESSAGE_MUTATION_BLOCKED);
      return;
    }
    const toRestore = Array.from(selectedMessages);
    if (toRestore.length === 0) return;

    setBulkActionLoading(true);
    try {
      // Remove from UI immediately
      setMessages(prev => prev.filter(msg => !selectedMessages.has(msg.mailId)));
      
      // Restore all in parallel
      await Promise.all(toRestore.map(id => {
        if (folder === 'archive') {
          return messageService.restoreFromArchive(id);
        } else {
          return messageService.restoreFromTrash(id);
        }
      }));
      
      // Invalidate cache, clear selection and refresh the list
      folderCache.delete(cacheKey);
      setSelectedMessages(new Set());
      setMessages([]);
      await loadMessages(false);
    } catch (err: any) {
      setError(err.message || 'Failed to restore some messages');
      // Invalidate cache and reload to restore state
      folderCache.delete(cacheKey);
      loadMessages(false);
    } finally {
      setBulkActionLoading(false);
    }
  };

  return (
    <Paper sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
      {!compactLandscape && (
      <Box
        sx={{
          p: { xs: 0.75, sm: 2 },
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: compactLandscape ? 0 : 1,
            gap: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
            <Typography variant={compactLandscape ? 'body2' : 'h6'} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
              {getFolderTitle()} ({filteredMessages.length})
            </Typography>
            {!compactLandscape && filteredMessages.length !== messages.length && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (of {messages.length})
              </Typography>
            )}
            {!compactLandscape && totalCount > 0 && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                — Showing {Math.min(((currentPage - 1) * pageSize) + 1, totalCount)}-{Math.min(currentPage * pageSize, totalCount)} of {totalCount}
              </Typography>
            )}
            {refreshing && !compactLandscape && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <CircularProgress size={16} sx={{ color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">Refreshing latest messages...</Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
            <Tooltip title="Refresh this folder">
              <IconButton size="small" onClick={handleManualRefresh}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Link
              component="button"
              variant="body2"
              onClick={toggleSearchSortMode}
              sx={{
                cursor: 'pointer',
                textDecoration: 'none',
                color: hasActiveFilters ? 'warning.main' : 'primary.main',
                '&:hover': {
                  textDecoration: 'underline',
                },
                fontSize: compactLandscape ? '0.75rem' : '0.875rem',
                whiteSpace: 'nowrap',
              }}
            >
              {searchSortMode ? 'Hide' : 'Search'}
            </Link>
          </Box>
        </Box>
        
        {!compactLandscape && searchFilterBar}
        {compactLandscape && searchSortMode && searchFilterBar}

        {/* Bulk Actions Bar - Only shown when searchSortMode is enabled and messages are selected */}
        {searchSortMode && selectedMessages.size > 0 && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            p: 1, 
            bgcolor: 'action.selected', 
            borderRadius: 1,
            mb: 1
          }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              {selectedMessages.size} selected
            </Typography>
            {/* Restore button for archive and trash folders */}
            {!isMinor && (folder === 'archive' || folder === 'trash') && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<RestoreIcon />}
                onClick={handleBulkRestore}
                disabled={bulkActionLoading}
              >
                Restore
              </Button>
            )}
            {/* Archive button for other folders */}
            {!isMinor && folder !== 'archive' && folder !== 'trash' && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<ArchiveIcon />}
                onClick={handleBulkArchive}
                disabled={bulkActionLoading}
              >
                Archive
              </Button>
            )}
            {/* Delete button for folders other than trash */}
            {!isMinor && folder !== 'trash' && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                disabled={bulkActionLoading}
              >
                Delete
              </Button>
            )}
            <Button
              size="small"
              onClick={() => setSelectedMessages(new Set())}
            >
              Clear
            </Button>
          </Box>
        )}
        {folder === 'sent' && !isMinor && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Pending messages can be canceled before delivery
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LockOpenIcon />}
              onClick={() => setReleaseDialogOpen(true)}
              sx={{ ml: 2 }}
            >
              Release Reserve
              <Tooltip title="Expires all undelivered messages, returning all reserved funds to your available balance. This will cancel delivery to all recipients who have not yet read their messages.">
                <InfoIcon sx={{ ml: 0.5, fontSize: 16, color: 'action.active' }} />
              </Tooltip>
            </Button>
          </Box>
        )}
        {folder === 'sent' && isMinor && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Pending messages can be canceled before delivery. Bulk reserve release is managed by a parent account.
          </Typography>
        )}
        {folder === 'trash' && (
          <Typography variant="caption" color="text.secondary">
            Messages in trash can be restored or permanently deleted
          </Typography>
        )}
        {folder === 'archive' && (
          <Typography variant="caption" color="text.secondary">
            Archived messages for long-term storage
          </Typography>
        )}
      </Box>
      )}

      <List sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 0, WebkitOverflowScrolling: 'touch' }}>
        {/* Select All Checkbox Header - Only shown when searchSortMode is enabled */}
        {searchSortMode && filteredMessages.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            px: { xs: 1, sm: 2 }, 
            py: 1, 
            borderBottom: 1, 
            borderColor: 'divider',
            bgcolor: 'action.hover'
          }}>
            <Checkbox
              checked={allFilteredSelected(filteredMessages)}
              indeterminate={someFilteredSelected(filteredMessages) && !allFilteredSelected(filteredMessages)}
              onChange={(e) => handleSelectAll(e.target.checked)}
              icon={<CheckBoxOutlineBlankIcon />}
              checkedIcon={<CheckBoxIcon />}
              size="small"
              sx={{
                p: 0.25,
                '& .MuiSvgIcon-root': {
                  fontSize: '1.1rem'
                }
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              Select all ({filteredMessages.length})
            </Typography>
          </Box>
        )}
        
        {filteredMessages.map((message, index) => {
          const status = (message.status || 'pending') as MessageStatus;
          const isDraftMessage = !!message.isDraft;
          const isIdentityDisclosureNotice = isIdentityDisclosureSystemNotice(message);
          const canRejectInboxMessage =
            folder === 'inbox' && !message.markRead && inboxMessageSupportsReject(message);
          // Credits/Debits folders only contain system receipts. FromName is often the
          // vendor/buyer (not *SYSTEM*), so do not require message.isSystemMessage alone.
          const isSystemLikeMessage = Boolean(
            message.isSystemMessage ||
            isIdentityDisclosureNotice ||
            folder === 'credits' ||
            folder === 'debits'
          );
          
          // Build From/To line based on folder
          const getFromToLine = () => {
            if (folder === 'inbox' || folder === 'archive') {
              const label = formatInboxFromLabel(message.fromName, message.fromAddress);
              return `From: ${label}`;
            }
            if (folder === 'credits') {
              // For credits (sales): show "Sold to: BuyerName"
              // The fromName field now contains the buyer's username
              const fromName = message.fromName?.trim();
              
              // If fromName is set and not *SYSTEM*, use it as "Sold to:"
              if (fromName && fromName !== '*SYSTEM*' && fromName !== 'SYSTEM') {
                return `Sold to: ${fromName}`;
              }
              
              // Fallback to transaction data if available
              const transactionLookupId = message.sourceTransactionId || message.mailId;
              const transaction = transactionCache.get(`${folder}:${transactionLookupId}`);
              if (transaction) {
                const rawType = transaction.rawType || transaction.rawTransactionType || '';
                
                // If user is the vendor/creator (CREDIT_PRODUCT_SALE), show buyer
                if (rawType === 'CREDIT_PRODUCT_SALE' && transaction.fromUser) {
                  return `Sold to: ${transaction.fromUser}`;
                }
                
                // If user is commission recipient (affiliate/forwarder), show vendor
                if (rawType === 'CREDIT_AFFILIATE_COMMISSION' || rawType === 'CREDIT_FORWARDER_COMMISSION') {
                  return `From: ${transaction.vendorId || transaction.toUser || 'Vendor'}`;
                }
                
                // For other credits (promo, etc.), show fromUser
                if (transaction.fromUser) {
                  return `From: ${transaction.fromUser}`;
                }
              }
              
              // Final fallback
              return `From: ${message.fromAddress?.split('@')[0] || 'Unknown'}`;
            }
            if (folder === 'debits') {
              // For debits (purchases): show "Paid to: VendorName"
              // The fromName field now contains the vendor's username
              const fromName = message.fromName?.trim();
              
              // If fromName is set and not *SYSTEM*, use it as "Paid to:"
              if (fromName && fromName !== '*SYSTEM*' && fromName !== 'SYSTEM') {
                return `Paid to: ${fromName}`;
              }
              
              // Fallback to transaction data if available
              const transactionLookupId = message.sourceTransactionId || message.mailId;
              const transaction = transactionCache.get(`${folder}:${transactionLookupId}`);
              if (transaction) {
                const rawType = transaction.rawType || transaction.rawTransactionType || '';
                
                // For purchases, show vendor (toUser)
                if (rawType === 'PURCHASE' && transaction.toUser) {
                  return `Paid to: ${transaction.toUser}`;
                }
                
                // For other debits, show toUser
                if (transaction.toUser) {
                  return `Paid to: ${transaction.toUser}`;
                }
              }
              
              // Final fallback
              const recipients = message.toRecipients?.map(r => r.split('@')[0]).join(', ') || 'Vendor';
              return `Paid to: ${recipients}`;
            }
            // For sent, trash, etc.
            const toList = message.toRecipients ?? [];
            if (toList.length === 0) return 'To: Unknown';
            const formatted = toList.map((addr) =>
              formatListSenderLabel(addr, getCachedNickname(addr))
            ).join(', ');
            return `To: ${formatted}`;
          };

          // Get postage and attached funds chips for this message (simplified - just amounts)
          const getPostageChips = () => {
            const chips: React.ReactNode[] = [];
            
            // Determine if this is a commercial message where advertiser paid fees
            // Gold chip indicates sender paid fees (recipient gets full amount)
            const isGoldChip = message.commercialMailing && message.feesPaidBySender;
            
            // Color scheme: Gold for sender-pays-fees commercial messages, Green for normal
            const chipColors = isGoldChip 
              ? { bg: '#fff8e1', text: '#f9a825', border: '#ffc107' }  // Gold/Amber
              : { bg: '#e8f5e9', text: '#2e7d32', border: '#4caf50' }; // Green
            
            if (folder === 'inbox') {
              if (isSystemLikeMessage) {
                return chips;
              }
              // Postage chip (credit) — always show, including $0.00
              const postage = message.requiredPostage ?? message.deliveryFee ?? 0;
              chips.push(
                <Chip
                  key="postage"
                  label={`$${postage.toFixed(2)}`}
                  size="small"
                  sx={{ 
                    height: 20, 
                    fontSize: '0.7rem', 
                    fontWeight: 'bold',
                    backgroundColor: chipColors.bg,
                    color: chipColors.text,
                    border: `1px solid ${chipColors.border}`
                  }}
                />
              );
              
              // Attached funds chip (credit) - black text to distinguish from postage
              // Only show if attachedAmount exists and is greater than 0
              const attachedAmount = message.attachedAmount ?? 0;
              if (attachedAmount > 0) {
                chips.push(
                  <Chip
                    key="attached"
                    label={`$${attachedAmount.toFixed(2)}`}
                    size="small"
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem', 
                      fontWeight: 'bold',
                      backgroundColor: chipColors.bg,
                      color: '#1a1a1a', // Black text to distinguish from postage
                      border: `1px solid ${chipColors.border}`
                    }}
                  />
                );
              }
            }
            
            if (folder === 'sent') {
              // Postage/fee chip (debit) - just the amount with minus
              const fee = message.deliveryFee ?? 0;
              if (fee > 0) {
                chips.push(
                  <Chip
                    key="postage"
                    label={`-$${fee.toFixed(2)}`}
                    size="small"
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem', 
                      fontWeight: 'bold',
                      backgroundColor: '#ffebee',
                      color: '#c62828',
                      border: '1px solid #ef5350'
                    }}
                  />
                );
              }
              
              // Attached funds chip (debit) - black text to distinguish from postage
              // Only show if attachedAmount exists and is greater than 0
              const attachedAmount = message.attachedAmount ?? 0;
              if (attachedAmount > 0) {
                chips.push(
                  <Chip
                    key="attached"
                    label={`-$${attachedAmount.toFixed(2)}`}
                    size="small"
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem', 
                      fontWeight: 'bold',
                      backgroundColor: '#ffebee',
                      color: '#1a1a1a', // Black text to distinguish from postage
                      border: '1px solid #ef5350'
                    }}
                  />
                );
              }
            }
            
            if (folder === 'credits') {
              // Credits: show total amount received (green chip)
              const totalAmount = message.totalAmount ?? message.moneyTotal ?? 0;
              if (totalAmount > 0) {
                chips.push(
                  <Chip
                    key="credit"
                    label={`+$${totalAmount.toFixed(2)}`}
                    size="small"
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem', 
                      fontWeight: 'bold',
                      backgroundColor: '#e8f5e9',
                      color: '#2e7d32',
                      border: '1px solid #4caf50'
                    }}
                  />
                );
              }
            }
            
            if (folder === 'debits') {
              // Debits: show total amount paid (red chip)
              const totalAmount = message.totalAmount ?? message.moneyTotal ?? 0;
              if (totalAmount > 0) {
                chips.push(
                  <Chip
                    key="debit"
                    label={`-$${totalAmount.toFixed(2)}`}
                    size="small"
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem', 
                      fontWeight: 'bold',
                      backgroundColor: '#ffebee',
                      color: '#c62828',
                      border: '1px solid #ef5350'
                    }}
                  />
                );
              }
            }
            
            return chips.length > 0 ? chips : null;
          };
          
          return (
            <React.Fragment key={`${message.mailId}-${index}`}>
              {/* 4-Column Compact Layout - Responsive */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  backgroundColor: message.markRead ? 'transparent' : 'action.hover',
                  '&:hover': { backgroundColor: 'action.selected' },
                }}
              >
                {/* Checkbox - Only shown when searchSortMode is enabled */}
                {searchSortMode && (
                  <Checkbox
                    checked={selectedMessages.has(message.mailId)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleSelectMessage(message.mailId, e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    icon={<CheckBoxOutlineBlankIcon />}
                    checkedIcon={<CheckBoxIcon />}
                    size="small"
                    sx={{ 
                      flexShrink: 0, 
                      mt: 0.5,
                      p: 0.25,
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.1rem'
                      }
                    }}
                  />
                )}
                
                <Box
                  onClick={() => {
                    // Intercept Cool/Archive messages — show retrieval modal first
                    if (message.blobTier === 'COOL' || message.blobTier === 'ARCHIVE') {
                      setRetrievalMessage({
                        mailId: message.mailId,
                        subject: message.subject || '(No Subject)',
                        blobTier: message.blobTier,
                        blobSizeBytes: message.blobSizeBytes ?? undefined,
                      });
                      setRetrievalModalOpen(true);
                    } else if (message.blobTier === 'OFFLOADED') {
                      // OFFLOADED: if locally stored, open for reading; else show info
                      if (localMailIds.has(message.mailId)) {
                        onMessageSelect(message.mailId);
                      } else {
                        setOffloadMessage({
                          mailId: message.mailId,
                          subject: message.subject || '(No Subject)',
                          fromName: message.fromName || message.fromAddress || '',
                          sentDate: message.sentDate || '',
                          sizeBytes: message.blobSizeBytes,
                        });
                        setOffloadModalOpen(true);
                      }
                    } else {
                      if ((folder === 'credits' || folder === 'debits') && isSystemLikeMessage) {
                        handleOpenLocalReceipt(message);
                      } else {
                        onMessageSelect(message.mailId);
                      }
                    }
                  }}
                  sx={{
                    display: 'grid',
                    flex: 1,
                    gridTemplateColumns: {
                      xs: 'minmax(0, 1fr) auto auto',  // 3 columns on mobile
                      sm: 'minmax(0, 1fr) auto auto auto',  // 4 columns on larger screens
                    },
                    gap: { xs: 0.5, sm: 1 },
                    px: { xs: 1, sm: 2 },
                    py: 1,
                    cursor: 'pointer',
                    alignItems: 'flex-start',
                    ml: searchSortMode ? -0.5 : 0, // Reduce space between checkbox and content only when checkbox is shown
                  }}
                >
                {/* INBOX 3-LINE LAYOUT */}
                {folder === 'inbox' && (
                  <>
                    {/* Column 1: 3 lines - Subject, From+Postage, Date+Attached/Category */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, gap: 0.25 }}>
                      {/* Line 1: Mail icon + Subject */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {message.markRead ? (
                          <DraftsOutlinedIcon color="action" fontSize="small" sx={{ flexShrink: 0 }} />
                        ) : (
                          <MailIcon color="primary" fontSize="small" sx={{ flexShrink: 0 }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: message.markRead ? 'normal' : 'bold',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                        >
                          {message.subject || '(No Subject)'}
                        </Typography>
                        <StorageTierBadge
                          tier={
                            message.blobTier === 'OFFLOADED'
                              ? (localMailIds.has(message.mailId) ? 'LOCAL' : 'UNAVAILABLE')
                              : message.blobTier
                          }
                          sizeBytes={message.blobSizeBytes ?? undefined}
                          onClick={(e) => {
                            e?.stopPropagation();
                            if (message.blobTier === 'OFFLOADED') {
                              setOffloadMessage({
                                mailId: message.mailId,
                                subject: message.subject || '(No Subject)',
                                fromName: message.fromName || message.fromAddress || '',
                                sentDate: message.sentDate || '',
                                sizeBytes: message.blobSizeBytes,
                              });
                              setOffloadModalOpen(true);
                            } else {
                              setRetrievalMessage({
                                mailId: message.mailId,
                                subject: message.subject || '(No Subject)',
                                blobTier: message.blobTier ?? 'HOT',
                                blobSizeBytes: message.blobSizeBytes,
                              });
                              setRetrievalModalOpen(true);
                            }
                          }}
                        />
                        {message.hasAttachments && (
                          <Tooltip title="Has attachments">
                            <AttachFileIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                          </Tooltip>
                        )}
                      </Box>
                      
                      {/* Line 2: From: [username] → Adjust icon → Postage chip (if sender override for marketing, or always for non-marketing) */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 3.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          From: {formatInboxFromLabel(message.fromName, message.fromAddress)}
                        </Typography>
                        {!isSystemLikeMessage && (
                        <Tooltip 
                          title={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                Current Rate: ${(message.requiredPostage ?? message.deliveryFee ?? 0.05).toFixed(2)}
                              </Typography>
                              <Typography variant="caption" component="p">
                                Click to adjust postage rate for this sender.
                                This rate will override the commercial postage rate for future messages.
                              </Typography>
                            </Box>
                          }
                          arrow
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleAdjustSenderRate(e, message); }}
                            sx={{ p: 0.25 }}
                          >
                            <EditIcon sx={{ fontSize: 12 }} color="primary" />
                          </IconButton>
                        </Tooltip>
                        )}
                        {/* Postage chip placement logic:
                            - Non-marketing: always show next to sender edit icon
                            - Marketing with SenderOverride rate type: show next to sender edit icon (line 2)
                            - Marketing with CategoryRate or DefaultRate: chip shows next to category edit icon on line 3
                            - Uses postageRateType from transaction (historical data) for read messages
                            - For unread messages, postageRateType is null; marketing chips may appear on line 3 instead */}
                        {(() => {
                          const isSenderOverride = message.postageRateType === 'SenderOverride';
                          const shouldShowChip = !message.commercialMailing || isSenderOverride;
                          const postage = message.requiredPostage ?? message.deliveryFee ?? 0;
                          
                          return shouldShowChip ? (
                            <Chip
                              label={`$${postage.toFixed(2)}`}
                              size="small"
                              sx={{ 
                                height: 18, 
                                fontSize: '0.75rem', 
                                fontWeight: 'bold',
                                backgroundColor: message.commercialMailing && message.feesPaidBySender ? '#fff8e1' : '#e8f5e9',
                                color: message.commercialMailing && message.feesPaidBySender ? '#f9a825' : '#2e7d32',
                                border: message.commercialMailing && message.feesPaidBySender ? '1px solid #ffc107' : '1px solid #4caf50'
                              }}
                            />
                          ) : null;
                        })()}
                      </Box>
                      
                      {/* Line 3: Date/Time → Attached (non-commercial) OR Category+Adjust (commercial) */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 3.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {fmtDateTimeShort(message.sentDate)}
                        </Typography>
                        
                        {/* Non-commercial: show attached amount if any */}
                        {/* Gold chip for commercial messages where advertiser paid fees */}
                        {!message.commercialMailing && (() => {
                          const attachedAmount = message.attachedAmount ?? 0;
                          return attachedAmount > 0 ? (
                            <Chip
                              label={`$${attachedAmount.toFixed(2)}`}
                              size="small"
                              sx={{ 
                                height: 18, 
                                fontSize: '0.75rem', 
                                fontWeight: 'bold',
                                backgroundColor: message.commercialMailing && message.feesPaidBySender ? '#fff8e1' : '#e8f5e9',
                                color: '#1a1a1a',
                                border: message.commercialMailing && message.feesPaidBySender ? '1px solid #ffc107' : '1px solid #4caf50'
                              }}
                            />
                          ) : null;
                        })()}
                        
                        {/* Commercial: show category chip + adjust icon + postage chip (if category rate applies) */}
                        {message.commercialMailing && !isSystemLikeMessage && (
                          <>
                            <Chip
                              label={message.commercialCategory || "Commercial"}
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.7rem' }}
                            />
                            <Tooltip 
                              title={
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Current Rate: ${(message.requiredPostage ?? message.deliveryFee ?? 0.05).toFixed(2)}
                                  </Typography>
                                  <Typography variant="caption" component="p">
                                    Click to adjust the commercial postage rate for &quot;{message.commercialCategory || 'Commercial'}&quot;.
                                    This affects all senders in this category for future messages.
                                  </Typography>
                                </Box>
                              }
                              arrow
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleAdjustCategoryRate(e, message); }}
                                sx={{ p: 0.25 }}
                              >
                                <EditIcon sx={{ fontSize: 12 }} color="primary" />
                              </IconButton>
                            </Tooltip>
                            {/* Postage chip for marketing messages - shows next to category edit when category rate applies (not SenderOverride) */}
                            {/* If SenderOverride rate type exists, chip is shown next to sender edit icon on line 2 instead */}
                            {/* Uses postageRateType from transaction (historical data) for read messages */}
                            {(() => {
                              // Use postageRateType from message (comes from transaction for read messages)
                              // Only show chip here if it's CategoryRate or DefaultRate (not SenderOverride)
                              // For unread messages, postageRateType is null, so we show chip here if it's marketing
                              const isSenderOverride = message.postageRateType === 'SenderOverride';
                              const postage = message.requiredPostage ?? message.deliveryFee ?? 0;
                              // Show chip here if:
                              // - It's a marketing message
                              // - Rate type is NOT SenderOverride (CategoryRate, DefaultRate, or null for unread)
                              // - Postage is always shown (including $0.00)
                              return !isSenderOverride ? (
                                <Chip
                                  label={`$${postage.toFixed(2)}`}
                                  size="small"
                                  sx={{ 
                                    height: 18, 
                                    fontSize: '0.75rem', 
                                    fontWeight: 'bold',
                                    backgroundColor: message.feesPaidBySender ? '#fff8e1' : '#e8f5e9',
                                    color: message.feesPaidBySender ? '#f9a825' : '#2e7d32',
                                    border: message.feesPaidBySender ? '1px solid #ffc107' : '1px solid #4caf50'
                                  }}
                                />
                              ) : null;
                            })()}
                          </>
                        )}
                      </Box>
                    </Box>
                    
                    {/* Column 2: Read/Unread status chip + Details link */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25, minWidth: 80 }}>
                      <Chip
                        label={message.markRead ? "Read" : (isSystemLikeMessage ? "System" : "Unread")}
                        size="small"
                        color={message.markRead ? "default" : "primary"}
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.75rem' }}
                      />
                      {isSystemLikeMessage ? null : (status === 'canceled' || status === 'error') ? (
                        <Typography 
                          variant="caption" 
                          color={status === 'canceled' ? 'text.disabled' : 'error.main'}
                          sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}
                        >
                          {status === 'canceled' ? 'Canceled' : (message.statusDescription || 'Failed')}
                        </Typography>
                      ) : !isSystemLikeMessage ? (
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          {/* Show regular account Details link */}
                          {/* Use sourceTransactionId when available (for system messages like receipts) */}
                          <Link
                            component="button"
                            variant="caption"
                            onClick={(e) => handleShowDetails(e, message.mailId)}
                            sx={{ 
                              color: 'primary.main',
                              textDecoration: 'none',
                              fontSize: '0.75rem',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            {transactionLoading ? '...' : 'Details'}
                          </Link>
                          {/* Show Vendor Credit Details link if vendor credits were used */}
                          {message.promoTransactionId && (
                            <>
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>|</Typography>
                              <Link
                                component="button"
                                variant="caption"
                                onClick={(e) => handleShowPromoDetails(e, message.promoTransactionId!)}
                                sx={{ 
                                  color: '#7b1fa2',
                                  textDecoration: 'none',
                                  fontSize: '0.75rem',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                {promoLoading ? '...' : 'Promo'}
                              </Link>
                            </>
                          )}
                        </Box>
                      ) : null}
                    </Box>
                  </>
                )}

                {/* SENT 3-LINE LAYOUT */}
                {folder === 'sent' && (
                  <>
                    {/* Column 1: 3 lines - Subject, To+Postage, Date+Attached */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, gap: 0.25 }}>
                      {/* Line 1: Status icon + Subject (unbold when delivered, like read inbox) */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isDraftMessage ? <DraftsOutlinedIcon fontSize="small" color="action" /> : getStatusIcon(status)}
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isDraftMessage ? 'bold' : (message.isDelivered ? 'normal' : 'bold'),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                        >
                          {message.subject || '(No Subject)'}
                        </Typography>
                        {message.hasAttachments && (
                          <Tooltip title="Has attachments">
                            <AttachFileIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                          </Tooltip>
                        )}
                      </Box>
                      
                      {/* Line 2: To: [recipients] → Postage chip */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 3.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          To: {message.toRecipients?.map(r => r.split('@')[0]).join(', ') || 'Unknown'}
                        </Typography>
                        {/* Postage chip (debit) */}
                        {(message.deliveryFee ?? 0) > 0 && (
                          <Chip
                            label={`-$${(message.deliveryFee ?? 0).toFixed(2)}`}
                            size="small"
                            sx={{ 
                              height: 18, 
                              fontSize: '0.75rem', 
                              fontWeight: 'bold',
                              backgroundColor: '#ffebee',
                              color: '#c62828',
                              border: '1px solid #ef5350'
                            }}
                          />
                        )}
                      </Box>
                      
                      {/* Line 3: Date/Time → Attached amount if any */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 3.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {fmtDateTimeShort(message.sentDate)}
                        </Typography>
                        {/* Attached amount chip (debit) */}
                        {(() => {
                          const attachedAmount = message.attachedAmount ?? 0;
                          return attachedAmount > 0 ? (
                            <Chip
                              label={`-$${attachedAmount.toFixed(2)}`}
                              size="small"
                              sx={{ 
                                height: 18, 
                                fontSize: '0.75rem', 
                                fontWeight: 'bold',
                                backgroundColor: '#ffebee',
                                color: '#1a1a1a',
                                border: '1px solid #ef5350'
                              }}
                            />
                          ) : null;
                        })()}
                      </Box>
                    </Box>
                    
                    {/* Column 2: Status chip + Details link */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25, minWidth: 50 }}>
                      <Chip
                        label={isDraftMessage ? 'Draft' : (message.statusDescription || getStatusLabel(status))}
                        size="small"
                        color={isDraftMessage ? 'default' : getStatusColor(status)}
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.75rem' }}
                      />
                      {isDraftMessage ? (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                          Click to resume
                        </Typography>
                      ) : (status === 'canceled' || status === 'error') ? (
                        <Typography 
                          variant="caption" 
                          color={status === 'canceled' ? 'text.disabled' : 'error.main'}
                          sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}
                        >
                          {status === 'canceled'
                            ? 'Canceled'
                            : (message.statusDescription || 'Failed')}
                        </Typography>
                      ) : isIdentityDisclosureSystemNotice(message) ? (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                          No fee details
                        </Typography>
                      ) : (
                        <Link
                          component="button"
                          variant="caption"
                          onClick={(e) => handleShowDetails(e, message.mailId)}
                          sx={{ 
                            color: 'primary.main',
                            textDecoration: 'none',
                            fontSize: '0.75rem',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {transactionLoading ? '...' : 'Details'}
                        </Link>
                      )}
                    </Box>
                  </>
                )}

                {/* OTHER FOLDERS LAYOUT (archive, trash, credits, debits) */}
                {folder !== 'inbox' && folder !== 'sent' && (
                  <>
                    {/* Column 1: Subject & From/To (with mail icon) */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                      {/* Mail icon */}
                      <Box sx={{ pt: 0.5, flexShrink: 0 }}>
                        {message.markRead ? (
                          <DraftsOutlinedIcon color="action" fontSize="small" />
                        ) : (
                          <MailIcon color="primary" fontSize="small" />
                        )}
                      </Box>
                      {/* Subject & From/To */}
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: message.markRead ? 'normal' : 'bold',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {message.subject || '(No Subject)'}
                        </Typography>
                        {message.hasAttachments && (
                          <Tooltip title="Has attachments">
                            <AttachFileIcon fontSize="small" color="action" sx={{ flexShrink: 0, ml: 0.5 }} />
                          </Tooltip>
                        )}
                        {/* For credits/debits: show from/to line with chips on the same line */}
                        {(folder === 'credits' || folder === 'debits') && getFromToLine() && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {getFromToLine()}
                            </Typography>
                            {getPostageChips()}
                          </Box>
                        )}
                        {/* For other folders: show from/to line without chips inline */}
                        {folder !== 'credits' && folder !== 'debits' && getFromToLine() && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                            }}
                          >
                            {getFromToLine()}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Column 2: Chips (postage, attached) - Only for non-credits/debits folders */}
                    {folder !== 'credits' && folder !== 'debits' && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end', minWidth: 80 }}>
                        {getPostageChips()}
                      </Box>
                    )}

                    {/* Column 3: Date & Details link */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 80 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(message.sentDate)}
                      </Typography>
                      {(status === 'canceled' || status === 'error') ? (
                        <Typography 
                          variant="caption" 
                          color={status === 'canceled' ? 'text.disabled' : 'error.main'}
                          sx={{ fontStyle: 'italic' }}
                        >
                          {status === 'canceled' ? 'Canceled' : (message.statusDescription || 'Failed')}
                        </Typography>
                      ) : isIdentityDisclosureSystemNotice(message) ? (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No fee details
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          {/* Use sourceTransactionId when available (for system messages like receipts) */}
                          <Link
                            component="button"
                            variant="caption"
                            onClick={(e) => handleShowDetails(e, message.mailId)}
                            sx={{ 
                              color: 'primary.main',
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            {transactionLoading ? '...' : 'Details'}
                          </Link>
                          {/* Show Vendor Credit Details link if vendor credits were used */}
                          {message.promoTransactionId && (
                            <>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>|</Typography>
                              <Link
                                component="button"
                                variant="caption"
                                onClick={(e) => handleShowPromoDetails(e, message.promoTransactionId!)}
                                sx={{ 
                                  color: '#7b1fa2',
                                  textDecoration: 'none',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                {promoLoading ? '...' : 'Promo'}
                              </Link>
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  </>
                )}

                {/* Column 3/4: Action icons - Archive & Delete available on all folders */}
                <Box sx={{ display: 'flex', gap: { xs: 0.25, sm: 0.5 }, alignItems: 'center', flexShrink: 0 }}>
                  {/* Sent folder specific actions (cancel, expire early) */}
                  {folder === 'sent' && !isDraftMessage && (
                    <>
                      {message.canCancel && (
                        <Tooltip title="Cancel">
                          <IconButton 
                            size="small"
                            color="warning"
                            onClick={(e) => { e.stopPropagation(); handleCancelClick(e, message.mailId); }}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {message.canExpireEarly && (
                        <Tooltip title="Expire early">
                          <IconButton 
                            size="small"
                            color="info"
                            onClick={(e) => { e.stopPropagation(); handleExpireClick(e, message.mailId); }}
                          >
                            <TimerIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                  
                  {/* Archive folder: Restore action */}
                  {!isMinor && folder === 'archive' && (
                    <Tooltip title="Restore to original folder">
                      <IconButton 
                        size="small"
                        color="primary"
                        onClick={(e) => { e.stopPropagation(); handleRestoreClick(e, message.mailId); }}
                        disabled={restoreLoading === message.mailId}
                      >
                        {restoreLoading === message.mailId ? (
                          <CircularProgress size={16} />
                        ) : (
                          <RestoreIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  {/* Trash folder: Restore action */}
                  {!isMinor && folder === 'trash' && (
                    <Tooltip title="Restore">
                      <IconButton 
                        size="small"
                        color="primary"
                        onClick={(e) => { e.stopPropagation(); handleRestoreClick(e, message.mailId); }}
                        disabled={restoreLoading === message.mailId}
                      >
                        {restoreLoading === message.mailId ? (
                          <CircularProgress size={16} />
                        ) : (
                          <RestoreIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  {/* Archive action - available on all folders except archive and trash */}
                  {!isMinor && folder !== 'archive' && folder !== 'trash' && !isDraftMessage && (
                    <Tooltip title="Archive">
                      <IconButton 
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleArchiveClick(e, message.mailId, message); }}
                      >
                        <ArchiveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  {/* Delete draft (local only — allowed for minors) */}
                  {isDraftMessage && folder !== 'trash' && (
                    <Tooltip title="Delete draft">
                      <IconButton 
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          const draftId = message.mailId.replace(/^draft:/, '');
                          if (user?.username) {
                            deleteDraft(user.username, draftId);
                            folderCache.delete(cacheKey);
                            removePersistedPage(user.username, folder, currentPage, pageSize);
                            loadMessages(false);
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Delete/Trash/Reject — blocked for minors */}
                  {!isMinor && !isDraftMessage && folder !== 'trash' && (
                    <Tooltip title={canRejectInboxMessage ? "Reject. Decline postage and delivery." : "Move to trash"}>
                      <IconButton 
                        size="small"
                        color={canRejectInboxMessage ? "error" : "default"}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTrashClick(e, message.mailId, message.markRead ?? true, message);
                        }}
                      >
                        {canRejectInboxMessage ? (
                          <CancelIcon fontSize="small" />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                </Box>
              </Box>
              {index < filteredMessages.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>

      <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={currentPage <= 1 || loading}
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
        >
          Previous
        </Button>
        <Typography variant="body2" color="text.secondary">
          Page {currentPage}{totalPages > 0 ? ` of ${totalPages}` : ''}
          {loading ? ' — Loading current page...' : ''}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          disabled={loading || totalPages <= 0 || currentPage >= totalPages}
          onClick={() => setCurrentPage(prev => prev + 1)}
        >
          Next
        </Button>
      </Box>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Message</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this message? This action cannot be undone.
            Any attached funds will be returned to your account.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelLoading}>
            Keep Message
          </Button>
          <Button 
            onClick={handleConfirmCancel} 
            color="error" 
            variant="contained"
            disabled={cancelLoading}
          >
            {cancelLoading ? <CircularProgress size={20} /> : 'Cancel Message'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Expire Early Confirmation Dialog */}
      <Dialog open={expireDialogOpen} onClose={() => setExpireDialogOpen(false)}>
        <DialogTitle>Expire Message Early</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will expire the message for recipients who haven't read it yet.
            Reserved funds for unread recipients will be returned to your account.
            Recipients who have already read the message will keep their copy.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpireDialogOpen(false)} disabled={expireLoading}>
            Keep Waiting
          </Button>
          <Button 
            onClick={handleConfirmExpire} 
            color="info" 
            variant="contained"
            disabled={expireLoading}
          >
            {expireLoading ? <CircularProgress size={20} /> : 'Expire Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Release Reserves Confirmation Dialog */}
      <Dialog open={releaseDialogOpen} onClose={() => { setReleaseDialogOpen(false); setReleaseResult(null); }}>
        <DialogTitle>
          {releaseResult ? 'Reserves Released' : 'Release All Reserved Funds'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {releaseResult ? (
              <>
                Successfully expired {releaseResult.expiredCount} message(s).
                <br />
                Released ${releaseResult.releasedAmount.toFixed(2)} back to your available balance.
              </>
            ) : (
              <>
                This will expire <strong>all undelivered messages</strong> and return all reserved funds to your available balance.
                <br /><br />
                • Messages that have been read by some recipients will remain delivered to them
                <br />
                • Messages that have not been read by any recipient will be fully canceled
                <br />
                • All reserved postage and attached funds will be released
                <br /><br />
                This action cannot be undone.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {releaseResult ? (
            <Button onClick={() => { setReleaseDialogOpen(false); setReleaseResult(null); }} color="primary">
              Close
            </Button>
          ) : (
            <>
              <Button onClick={() => setReleaseDialogOpen(false)} disabled={releaseLoading}>
                Cancel
              </Button>
              <Button 
                onClick={handleReleaseReserves} 
                color="warning" 
                variant="contained"
                disabled={releaseLoading}
              >
                {releaseLoading ? <CircularProgress size={20} /> : 'Release All Reserves'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Pending Message Warning Dialog */}
      <Dialog open={pendingWarningOpen} onClose={() => setPendingWarningOpen(false)} data-message-id={pendingMessageId}>
        <DialogTitle>Message Awaiting Delivery</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This message is still awaiting delivery and has reserved funds.
            <br /><br />
            To {pendingAction === 'archive' ? 'archive' : 'delete'} this message, you must first <strong>expire it</strong> or <strong>cancel it</strong> to release the reserved funds.
            <br /><br />
            Use the <strong>Cancel</strong> button (🚫) to cancel and return all funds, or the <strong>Expire Early</strong> button (⏱) to expire for unread recipients while keeping delivery for those who already read it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingWarningOpen(false)} color="primary" variant="contained">
            OK, Got It
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Details Dialog - Using shared component */}
      <TransactionDetailsDialog
        open={transactionDialogOpen}
        onClose={() => setTransactionDialogOpen(false)}
        transaction={selectedTransaction}
      />
      
      {/* Vendor Credit Transaction Details Dialog - For purchases using vendor credits */}
      <PromoTransactionDetailsDialog
        open={promoDialogOpen}
        onClose={() => setPromoDialogOpen(false)}
        transaction={selectedPromoTransaction}
        onOpenLinkedTransaction={handleOpenLinkedRegularTransaction}
      />

      <LocalReceiptPreviewDialog
        open={receiptDialogOpen}
        onClose={() => {
          setReceiptDialogOpen(false);
          setReceiptDetail(null);
          setReceiptError(null);
          setReceiptLoading(false);
        }}
        detail={receiptDetail}
        loading={receiptLoading}
        error={receiptError}
      />

      {/* Cool / Archive storage retrieval modal */}
      <RetrievalModal
        open={retrievalModalOpen}
        onClose={() => { setRetrievalModalOpen(false); setRetrievalMessage(null); }}
        mailId={retrievalMessage?.mailId}
        subject={retrievalMessage?.subject}
        blobTier={retrievalMessage?.blobTier}
        blobSizeBytes={retrievalMessage?.blobSizeBytes ?? undefined}
        onRetrieved={(mailId) => { onMessageSelect(mailId); }}
        onListRefresh={() => {
          setMessages([]);
          loadMessages(false);
        }}
      />

      {/* Phase 3: Offload modal (download to device) */}
      {offloadMessage && (
        <OffloadModal
          open={offloadModalOpen}
          mailId={offloadMessage.mailId}
          subject={offloadMessage.subject}
          fromName={offloadMessage.fromName}
          sentDate={offloadMessage.sentDate}
          sizeBytes={offloadMessage.sizeBytes}
          allowServerDelete={!isMinor}
          onClose={() => { setOffloadModalOpen(false); setOffloadMessage(null); }}
          onOffloadComplete={(mailId) => {
            // Mark as locally available in state
            setLocalMailIds(prev => new Set([...prev, mailId]));
            setOffloadModalOpen(false);
            setOffloadMessage(null);
          }}
        />
      )}

      {rateAdjustMessage && user && (
        <>
          <AdjustSenderRateDialog
            open={adjustSenderRateOpen}
            onClose={() => {
              setAdjustSenderRateOpen(false);
              setRateAdjustMessage(null);
            }}
            username={user.username}
            fromName={rateAdjustMessage.fromName}
            fromAddress={rateAdjustMessage.fromAddress}
            commercialMailing={rateAdjustMessage.commercialMailing}
            commercialCategory={rateAdjustMessage.commercialCategory}
            actualPostageReceived={rateAdjustMessage.requiredPostage ?? rateAdjustMessage.deliveryFee ?? 0}
            onSaved={handleRateAdjustSaved}
          />
          <AdjustCategoryRateDialog
            open={adjustCategoryRateOpen}
            onClose={() => {
              setAdjustCategoryRateOpen(false);
              setRateAdjustMessage(null);
            }}
            commercialCategory={rateAdjustMessage.commercialCategory}
            commercialCode={rateAdjustMessage.commercialCode}
            initialRate={rateAdjustMessage.requiredPostage ?? rateAdjustMessage.deliveryFee ?? 0.05}
            onSaved={handleRateAdjustSaved}
          />
        </>
      )}
    </Paper>
  );
};

export default MessageList;
